import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { Tab, Tabs, Typography, CircularProgress } from '@mui/material';
import Modal from '@mui/material/Modal';
import EventHeader from './components/common/EventHeader';
import EventInsights from './components/common/EventInsights';
import EditSwarmAndEventForm from './components/configuration/EditSwarmAndEventForm';
import TabPanel from './components/common/TabPanel';
import AutobuyContent from './components/autobuy/AutobuyContent';
import useSwarmData from './hooks/useSwarmData';

/**
 * Modal component for editing swarm events
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is open
 * @param {() => void} props.onClose - Function to call when the modal should close
 * @param {number} props.bteId - The BTE ID to fetch swarm data
 * @param {SwarmAdminToolRes} props.swarm - Swarm object with event data
 * @param {number} [props.initialTabIndex=0] - Initial tab index to show (0 for Configuration, 1 for Autobuy)
 * @returns {JSX.Element} The SwarmEditModal component
 */
export default function SwarmEditModal({ isOpen, onClose, bteId, swarmProp, initialTabIndex = 0 }) {
  // Use our custom hook for swarm data loading and validation
  //TODO: we need to check if it necessary to use this hook, because we are receiving the swarm data as a prop
  const { 
    swarm, 
    event, 
    isLoading, 
    isFetching, 
    isError, 
    error, 
    isValid 
  } = useSwarmData(bteId, isOpen);
  
  const [tabValue, setTabValue] = useState(initialTabIndex);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Loading state
  if(isLoading || isFetching){
    return (
      <ModalWrapper open={isOpen} handleClose={onClose}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={24} />
        </Box>
      </ModalWrapper>
    );
  }

  // Error state
  if(isError){
    return (
      <ModalWrapper open={isOpen} handleClose={onClose}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">Error loading swarm data: {error?.message}</Typography>
        </Box>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper open={isOpen} handleClose={onClose}>
      {isValid ? (
        <>
          <EventHeader
            name={event.name}
            date={event.date}
            venue={event.venue.name}
            city={event.venue.city}
          />

          <EventInsights bteId={bteId} />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Configuration" />
              <Tab label="Autobuy" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            <EditSwarmAndEventForm 
              swarm={swarm} 
              event={event} 
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <AutobuyContent 
              swarmId={swarm?.id} 
              isAutoBuyActive={swarmProp?.isAutoBuyActive || false} 
            />
          </TabPanel>
        </>
      ) : (
        <Typography>No data available</Typography>
      )}
    </ModalWrapper>
  );
}

function ModalWrapper({ children, open, handleClose }) {
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-describedby="modal-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        border: 'none',
        borderRadius: '1.5em',
        boxShadow: 24,
        p: 3,
      }}>
        {children}
      </Box>
    </Modal>
  );
}
